import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PatchTransportTypeInput, TransportType, TransportTypeCategory } from '@magimp/shared';
import {
  Button,
  DataTable,
  EditorModal,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Select,
  ToolbarActions,
  useEditorGuard,
} from '../../../ui/index.js';
import {
  activateTransportType,
  archiveTransportType,
  createTransportType,
  listTransportTypes,
  patchTransportType,
} from '../../../api/transport-types.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { TransportTypeForm } from './TransportTypeForm.js';
import {
  buildCreate,
  diffPatch,
  emptyForm,
  formFromRow,
  isCreateFormDirty,
  parseDecimal,
  type DraftForm,
} from './transport-type-form-model.js';

type StatusFilter = 'active' | 'inactive' | 'all';
type CategoryFilter = TransportTypeCategory | 'all';

const TRANSPORT_TYPES_QK = ['transport-types-catalog'] as const;

export function TransportTypesPage() {
  const { t } = useTranslation(['transportTypes', 'common']);
  // WHY (#623): the route gate only requires `transportTypes.read`; a
  // user without `transportTypes.manage` would otherwise see Create /
  // Edit / Archive / Restore buttons whose backend POST/PATCH/DELETE
  // returns 403. Hide them up front so the UI contract matches the
  // server policy.
  const permissions = useAuthStore((s) => s.permissions);
  const canManage = hasPermission(permissions, 'transportTypes.manage');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<TransportType | null>(null);

  const query = useQuery({
    queryKey: [...TRANSPORT_TYPES_QK, status, category, q],
    queryFn: () => listForFilter(status, category, q),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>
          {t('transportTypes:title')}
        </h1>
        <ToolbarActions>
          <Input
            value={q}
            onChange={setQ}
            icon="search"
            placeholder={t('transportTypes:search')}
            maxLength={200}
          />
          <Select
            value={category}
            onChange={(v) => setCategory(v as CategoryFilter)}
            options={[
              { value: 'all', label: t('transportTypes:filters.categoryAll') },
              { value: 'truck', label: t('transportTypes:category.truck') },
              { value: 'container', label: t('transportTypes:category.container') },
            ]}
            style={{ width: 170 }}
          />
          <Select
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            options={[
              { value: 'active', label: t('transportTypes:filters.active') },
              { value: 'inactive', label: t('transportTypes:filters.inactive') },
              { value: 'all', label: t('transportTypes:filters.all') },
            ]}
            style={{ width: 150 }}
          />
          {canManage && (
            <Button kind="primary" icon="plus" onClick={() => setCreateOpen(true)}>
              {t('transportTypes:actions.create')}
            </Button>
          )}
        </ToolbarActions>
      </header>
      {query.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(query.error)}
          onRetry={() => void query.refetch()}
        />
      )}
      {query.isPending && <LoadingState />}
      {query.isSuccess && (
        <DataTable
          rows={query.data}
          onSelect={setSelected}
          emptyState={
            <EmptyState title={t('common:states.emptyTitle')} text={t('transportTypes:empty')} />
          }
          columns={[
            { key: 'sortOrder', label: t('transportTypes:columns.sortOrder'), width: 70 },
            { key: 'code', label: t('transportTypes:columns.code'), sortable: true },
            {
              key: 'name',
              label: t('transportTypes:columns.name'),
              sortable: true,
              sortValue: (row) => row.nameRu,
              render: (row) => row.nameRu,
            },
            {
              key: 'category',
              label: t('transportTypes:columns.category'),
              width: 130,
              render: (row) => t(`transportTypes:category.${row.category}` as const),
            },
            {
              key: 'volume',
              label: t('transportTypes:columns.volume'),
              width: 130,
              align: 'right',
              render: (row) =>
                t('transportTypes:range', { min: row.volumeMinM3, max: row.volumeMaxM3 }),
            },
            {
              key: 'payload',
              label: t('transportTypes:columns.payload'),
              width: 130,
              align: 'right',
              render: (row) =>
                t('transportTypes:range', { min: row.payloadMinT, max: row.payloadMaxT }),
            },
            {
              key: 'status',
              label: t('transportTypes:columns.status'),
              width: 110,
              render: (row) =>
                row.active
                  ? t('transportTypes:status.active')
                  : t('transportTypes:status.inactive'),
              sortable: true,
              sortValue: (row) => (row.active ? 1 : 0),
            },
          ]}
        />
      )}
      {createOpen && canManage && <TransportTypeCreateModal onClose={() => setCreateOpen(false)} />}
      {selected && (
        <TransportTypeEditorModal
          row={selected}
          canManage={canManage}
          onClose={() => setSelected(null)}
          onUpdated={setSelected}
        />
      )}
    </div>
  );
}

async function listForFilter(
  status: StatusFilter,
  category: CategoryFilter,
  q: string,
): Promise<TransportType[]> {
  const base: { q?: string; category?: TransportTypeCategory; limit: number } = { limit: 100 };
  if (q.trim()) base.q = q.trim();
  if (category !== 'all') base.category = category;
  if (status === 'active') return (await listTransportTypes({ ...base, active: true })).items;
  if (status === 'inactive') return (await listTransportTypes({ ...base, active: false })).items;
  const [active, inactive] = await Promise.all([
    listTransportTypes({ ...base, active: true }),
    listTransportTypes({ ...base, active: false }),
  ]);
  return [...active.items, ...inactive.items];
}

function TransportTypeCreateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['transportTypes', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const [form, setForm] = useState<DraftForm>(emptyForm);

  const payload = useMemo(() => buildCreate(form), [form]);
  const formError = useMemo(() => {
    const volumeMin = parseDecimal(form.volumeMin);
    const volumeMax = parseDecimal(form.volumeMax);
    if (Number.isFinite(volumeMin) && Number.isFinite(volumeMax) && volumeMax < volumeMin) {
      return t('transportTypes:errors.volumeRange');
    }
    const payloadMin = parseDecimal(form.payloadMin);
    const payloadMax = parseDecimal(form.payloadMax);
    if (Number.isFinite(payloadMin) && Number.isFinite(payloadMax) && payloadMax < payloadMin) {
      return t('transportTypes:errors.payloadRange');
    }
    return null;
  }, [form, t]);

  const mutation = useMutation({
    mutationFn: createTransportType,
    onSuccess: () => {
      // Reset the form so the guard sees a clean baseline before close.
      setForm(emptyForm());
      void qc.invalidateQueries({ queryKey: TRANSPORT_TYPES_QK });
      showToast({ tone: 'success', text: t('transportTypes:toasts.created') });
      onClose();
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  // WHY: dirty for create = trimmed/normalized form differs from the
  // empty defaults. Compare against `emptyForm()` field-by-field so pure
  // whitespace edits and the `sortOrder: '0'` baseline do not falsely
  // trigger the unsaved-changes prompt.
  const dirty = isCreateFormDirty(form);

  const canSubmit = payload !== null && formError === null && !mutation.isPending;

  return (
    <EditorModal
      open
      title={t('transportTypes:modals.createTitle')}
      width={640}
      dirty={dirty}
      busy={mutation.isPending}
      onConfirmClose={onClose}
    >
      <TransportTypeForm form={form} onChange={setForm} mode="create" error={formError} />
      <EditorFooter>
        <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
        <Button
          kind="primary"
          disabled={!canSubmit}
          onClick={() => payload && mutation.mutate(payload)}
        >
          {t('common:actions.create')}
        </Button>
      </EditorFooter>
    </EditorModal>
  );
}

function TransportTypeEditorModal({
  row,
  canManage,
  onClose,
  onUpdated,
}: {
  row: TransportType;
  canManage: boolean;
  onClose: () => void;
  onUpdated: (row: TransportType) => void;
}) {
  const { t } = useTranslation(['transportTypes', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const [form, setForm] = useState<DraftForm>(() => formFromRow(row));

  const patch = useMemo(() => diffPatch(row, form), [row, form]);
  const formError = useMemo(() => {
    const volumeMin = parseDecimal(form.volumeMin);
    const volumeMax = parseDecimal(form.volumeMax);
    if (Number.isFinite(volumeMin) && Number.isFinite(volumeMax) && volumeMax < volumeMin) {
      return t('transportTypes:errors.volumeRange');
    }
    const payloadMin = parseDecimal(form.payloadMin);
    const payloadMax = parseDecimal(form.payloadMax);
    if (Number.isFinite(payloadMin) && Number.isFinite(payloadMax) && payloadMax < payloadMin) {
      return t('transportTypes:errors.payloadRange');
    }
    return null;
  }, [form, t]);

  const patchMutation = useMutation({
    mutationFn: (body: PatchTransportTypeInput) => patchTransportType(row.id, body),
    onSuccess: (next) => {
      // Reset baseline so the guard sees clean state before close paths.
      setForm(formFromRow(next));
      void qc.invalidateQueries({ queryKey: TRANSPORT_TYPES_QK });
      showToast({ tone: 'success', text: t('transportTypes:toasts.updated') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveTransportType(row.id),
    onSuccess: (next) => {
      void qc.invalidateQueries({ queryKey: TRANSPORT_TYPES_QK });
      showToast({ tone: 'success', text: t('transportTypes:toasts.archived') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const activateMutation = useMutation({
    mutationFn: () => activateTransportType(row.id),
    onSuccess: (next) => {
      void qc.invalidateQueries({ queryKey: TRANSPORT_TYPES_QK });
      showToast({ tone: 'success', text: t('transportTypes:toasts.activated') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const canSave = patch !== null && formError === null && !patchMutation.isPending;
  const busy = patchMutation.isPending || archiveMutation.isPending || activateMutation.isPending;
  // WHY: dirty for edit = the diff helper reports a non-empty patch
  // against the loaded `row`. Trim happens inside `diffPatch`, so this
  // matches the save path's wire body exactly.
  const dirty = patch !== null;

  return (
    <EditorModal
      open
      title={t('transportTypes:modals.editTitle', { name: row.nameRu })}
      width={640}
      dirty={dirty}
      busy={busy}
      onConfirmClose={onClose}
    >
      <TransportTypeForm
        form={form}
        onChange={setForm}
        mode="edit"
        error={formError}
        readOnly={!canManage}
      />
      <EditorFooter>
        {canManage &&
          (row.active ? (
            <Button kind="ghost" disabled={busy} onClick={() => archiveMutation.mutate()}>
              {t('common:actions.archive')}
            </Button>
          ) : (
            <Button kind="ghost" disabled={busy} onClick={() => activateMutation.mutate()}>
              {t('common:actions.restore')}
            </Button>
          ))}
        <CancelButton onClose={onClose} label={t('common:actions.close')} />
        {canManage && (
          <Button
            kind="primary"
            disabled={!canSave}
            onClick={() => patch && patchMutation.mutate(patch)}
          >
            {t('common:actions.save')}
          </Button>
        )}
      </EditorFooter>
    </EditorModal>
  );
}

// WHY: Cancel must call `useEditorGuard().confirmDestructive(onClose)` to
// prompt on dirty close. The EditorGuardContext provider only wraps the
// EditorModal `children` subtree, not the `footer` prop, so the action
// buttons are rendered inline at the bottom of children. `EditorFooter`
// mirrors the visual style of the Modal footer slot.
function EditorFooter({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 12,
        marginLeft: -16,
        marginRight: -16,
        marginBottom: -16,
        padding: '12px 16px',
        borderTop: '1px solid var(--border-2)',
        display: 'flex',
        gap: 8,
        justifyContent: 'flex-end',
      }}
    >
      {children}
    </div>
  );
}

function CancelButton({ onClose, label }: { onClose: () => void; label: string }) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}
